import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { usePermissions } from '../../hooks/usePermissions';
import { DISPLAY_PERMISSIONS, PERMISSION_GROUPS, resolvePermissionForApi } from '../../constants/permissions';

const VACANCY_ACCESS_EXCLUSIVE_GROUP = 'vacancy-access-level';
const FULL_VACANCY_ACCESS_PERMISSION = 'view_any_vacancy';
const LIMITED_VACANCY_ACCESS_PERMISSION = 'view_vacancy';
const BROADCAST_PARENT_PERMISSION = 'change_vacancy_broadcast_for_moderation_action';
const BROADCAST_FULL_PERMISSION = 'change_vacancy_full_broadcast';
const BROADCAST_LIMITED_PERMISSION = 'change_vacancy_limited_broadcast';
const ACCEPT_MODERATION_PERMISSION = 'accept_moderation';
const EMPLOYERS_LIMIT_PERMISSIONS = ['change_user_employers', 'change_user_max_employers', 'change_user_auto_employers', 'change_user_supervisor'];
const getPermissionsSetKey = (permissions = []) => permissions.slice().sort().join('|');
const getItemPriority = (item) => {
  if (typeof item.exclusivePriority === 'number') {
    return item.exclusivePriority;
  }

  const label = (item.label || '').toLowerCase();
  const hasFullHint = label.includes('полный') || label.includes('полные');
  return (hasFullHint ? 1000 : 0) + (item.permissions || []).length;
};

const isEmployersLimitsItem = (item) => {
  const itemPermissions = item?.permissions || [];
  if (itemPermissions.length !== EMPLOYERS_LIMIT_PERMISSIONS.length) {
    return false;
  }

  return EMPLOYERS_LIMIT_PERMISSIONS.every((permission) => itemPermissions.includes(permission));
};

const filterTreeItems = (items, shouldHideEmployersLimits) => {
  return items.reduce((acc, item) => {
    if (shouldHideEmployersLimits && isEmployersLimitsItem(item)) {
      return acc;
    }

    const nextItem = { ...item };
    if (item.children?.length > 0) {
      nextItem.children = filterTreeItems(item.children, shouldHideEmployersLimits);
    }

    acc.push(nextItem);
    return acc;
  }, []);
};

export default function PermissionsCheckboxList({ form, name = 'permissions', userPermissions = null, disabled = false, hideEmployersLimitsOption = false }) {
  const { permissions: currentUserPermissions, isSuperuser } = usePermissions();
  const selectedPermissions = form.watch(name) || {};
  const isFullVacancyAccessActive = Boolean(selectedPermissions[FULL_VACANCY_ACCESS_PERMISSION]);
  const filteredPermissionGroups = useMemo(() => {
    return PERMISSION_GROUPS.map((group) => ({
      ...group,
      items: filterTreeItems(group.items || [], hideEmployersLimitsOption),
    })).filter((group) => group.items.length > 0);
  }, [hideEmployersLimitsOption]);
  const permissionTreeItems = useMemo(() => filteredPermissionGroups.flatMap((group) => group.items), [filteredPermissionGroups]);
  const basicPermissions = useMemo(() => {
    const basicGroup = PERMISSION_GROUPS.find((group) => group.title === 'Основные полномочия');
    if (!basicGroup) {
      return new Set();
    }

    const set = new Set();
    const walk = (items) => {
      items.forEach((item) => {
        (item.permissions || []).forEach((permission) => set.add(permission));
        if (item.children?.length > 0) {
          walk(item.children);
        }
      });
    };

    walk(basicGroup.items || []);
    return set;
  }, []);

  const hasPermission = (permission) => {
    const apiPermission = resolvePermissionForApi(permission);
    return currentUserPermissions.some((p) => p.name === apiPermission || p === apiPermission);
  };
  const isLimitedVacancyAccessItem = (item) => item.exclusiveGroup === VACANCY_ACCESS_EXCLUSIVE_GROUP && (item.permissions || []).includes(LIMITED_VACANCY_ACCESS_PERMISSION);
  const isFullVacancyAccessItem = (item) => item.exclusiveGroup === VACANCY_ACCESS_EXCLUSIVE_GROUP && (item.permissions || []).includes(FULL_VACANCY_ACCESS_PERMISSION);

  const limitedVacancyAccessNode = useMemo(() => {
    const findLimitedVacancyNode = (items) => {
      for (const item of items) {
        if (isLimitedVacancyAccessItem(item)) {
          return item;
        }
        if (item.children?.length > 0) {
          const foundInChildren = findLimitedVacancyNode(item.children);
          if (foundInChildren) {
            return foundInChildren;
          }
        }
      }

      return null;
    };

    return findLimitedVacancyNode(permissionTreeItems);
  }, [permissionTreeItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const normalizedPermissions = Array.isArray(userPermissions) ? userPermissions : [];
    const permissionValues = {};

    DISPLAY_PERMISSIONS.forEach((permission) => {
      const apiPermission = resolvePermissionForApi(permission);
      permissionValues[permission] = normalizedPermissions.some((p) => p.name === permission || p === permission || p.name === apiPermission || p === apiPermission);
    });

    form.setValue(name, permissionValues);
  }, [userPermissions, form, name]);

  const exclusiveGroups = useMemo(() => {
    const groups = {};

    const walk = (items) => {
      items.forEach((item) => {
        if (item.exclusiveGroup && item.permissions?.length > 0) {
          if (!groups[item.exclusiveGroup]) {
            groups[item.exclusiveGroup] = [];
          }
          groups[item.exclusiveGroup].push(item.permissions);
        }
        if (item.children?.length > 0) {
          walk(item.children);
        }
      });
    };

    walk(permissionTreeItems);
    return groups;
  }, [permissionTreeItems]);

  const exclusiveOptionPriority = useMemo(() => {
    const priorityByOption = {};

    const walk = (items) => {
      items.forEach((item) => {
        if (item.exclusiveGroup && item.permissions?.length > 0) {
          priorityByOption[`${item.exclusiveGroup}::${getPermissionsSetKey(item.permissions)}`] = getItemPriority(item);
        }
        if (item.children?.length > 0) {
          walk(item.children);
        }
      });
    };

    walk(permissionTreeItems);
    return priorityByOption;
  }, [permissionTreeItems]);
  const exclusiveOptionsByGroup = useMemo(() => {
    const optionsByGroup = {};

    const walk = (items) => {
      items.forEach((item) => {
        if (item.exclusiveGroup && item.permissions?.length > 0) {
          if (!optionsByGroup[item.exclusiveGroup]) {
            optionsByGroup[item.exclusiveGroup] = [];
          }

          const optionKey = `${item.exclusiveGroup}::${getPermissionsSetKey(item.permissions)}`;
          if (!optionsByGroup[item.exclusiveGroup].some((option) => option.key === optionKey)) {
            optionsByGroup[item.exclusiveGroup].push({
              key: optionKey,
              permissions: item.permissions,
              priority: getItemPriority(item),
            });
          }
        }

        if (item.children?.length > 0) {
          walk(item.children);
        }
      });
    };

    walk(permissionTreeItems);
    return optionsByGroup;
  }, [permissionTreeItems]);
  const exclusiveOptionsByPermission = useMemo(() => {
    const optionsByPermission = {};

    Object.entries(exclusiveOptionsByGroup).forEach(([exclusiveGroup, options]) => {
      options.forEach((option) => {
        option.permissions.forEach((permission) => {
          if (!optionsByPermission[permission]) {
            optionsByPermission[permission] = [];
          }

          optionsByPermission[permission].push({
            exclusiveGroup,
            priority: option.priority,
          });
        });
      });
    });

    return optionsByPermission;
  }, [exclusiveOptionsByGroup]);

  const fullVacancyImpliedPermissions = useMemo(() => {
    if (!limitedVacancyAccessNode) {
      return [];
    }

    const impliedPermissions = new Set([LIMITED_VACANCY_ACCESS_PERMISSION]);
    const bestByExclusiveGroup = {};

    const walk = (items) => {
      items.forEach((item) => {
        const permissions = item.permissions || [];

        if (permissions.length > 0) {
          if (item.exclusiveGroup) {
            const currentBest = bestByExclusiveGroup[item.exclusiveGroup];
            const currentPriority = getItemPriority(item);
            if (!currentBest || currentPriority > currentBest.priority) {
              bestByExclusiveGroup[item.exclusiveGroup] = { permissions, priority: currentPriority };
            }
          } else {
            permissions.forEach((permission) => {
              if (basicPermissions.has(permission)) {
                return;
              }
              impliedPermissions.add(permission);
            });
          }
        }

        if (item.children?.length > 0) {
          walk(item.children);
        }
      });
    };

    walk(limitedVacancyAccessNode.children || []);

    Object.values(bestByExclusiveGroup).forEach(({ permissions }) => {
      permissions.forEach((permission) => {
        if (basicPermissions.has(permission)) {
          return;
        }
        impliedPermissions.add(permission);
      });
    });

    return Array.from(impliedPermissions);
  }, [limitedVacancyAccessNode, basicPermissions]);
  const fullVacancyAssignablePermissions = useMemo(() => {
    return new Set([FULL_VACANCY_ACCESS_PERMISSION, ...fullVacancyImpliedPermissions]);
  }, [fullVacancyImpliedPermissions]);

  const hasAssignableOptionInExclusiveGroup = (exclusiveGroup, minPriority) => {
    const options = exclusiveOptionsByGroup[exclusiveGroup] || [];

    return options.some((option) => {
      if (option.priority < minPriority) {
        return false;
      }

      return option.permissions.every((permission) => hasPermission(permission));
    });
  };

  const canAssignPermission = (permission) => {
    if (isSuperuser) return true;
    if (hasPermission(permission)) return true;

    const relatedExclusiveOptions = exclusiveOptionsByPermission[permission] || [];
    const canAssignFromExclusiveGroup = relatedExclusiveOptions.some(({ exclusiveGroup, priority }) => hasAssignableOptionInExclusiveGroup(exclusiveGroup, priority));
    if (canAssignFromExclusiveGroup) {
      return true;
    }

    if (hasPermission(FULL_VACANCY_ACCESS_PERMISSION) && fullVacancyAssignablePermissions.has(permission)) {
      return true;
    }

    return false;
  };

  const getIsDisabled = (permissions) => {
    if (disabled) return true;
    return permissions.some((permission) => !canAssignPermission(permission));
  };

  const isSamePermissionsSet = (left, right) => {
    if (left.length !== right.length) return false;
    return left.every((permission) => right.includes(permission));
  };

  const setPermissionValueIfChanged = (permission, value) => {
    if (Boolean(selectedPermissions[permission]) === value) {
      return;
    }

    form.setValue(`${name}.${permission}`, value, { shouldDirty: true });
  };

  useEffect(() => {
    if (!isFullVacancyAccessActive) {
      return;
    }

    fullVacancyImpliedPermissions.forEach((permission) => {
      if (!canAssignPermission(permission)) {
        return;
      }

      setPermissionValueIfChanged(permission, true);
    });
  }, [isFullVacancyAccessActive, fullVacancyImpliedPermissions, currentUserPermissions, isSuperuser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isBroadcastParentEnabled = Boolean(selectedPermissions[BROADCAST_PARENT_PERMISSION]);
    const hasBroadcastChildPermission = Boolean(selectedPermissions[BROADCAST_FULL_PERMISSION] || selectedPermissions[BROADCAST_LIMITED_PERMISSION]);

    if (!isBroadcastParentEnabled) {
      setPermissionValueIfChanged(BROADCAST_FULL_PERMISSION, false);
      setPermissionValueIfChanged(BROADCAST_LIMITED_PERMISSION, false);
      return;
    }

    // "Разместить" подразумевает accept_moderation в payload,
    // но чекбокс "Согласовать" должен оставаться визуально снятым.
    setPermissionValueIfChanged(ACCEPT_MODERATION_PERMISSION, false);

    if (hasBroadcastChildPermission) {
      return;
    }

    const defaultBroadcastPermission = [BROADCAST_LIMITED_PERMISSION, BROADCAST_FULL_PERMISSION].find((permission) => canAssignPermission(permission));
    if (defaultBroadcastPermission) {
      setPermissionValueIfChanged(defaultBroadcastPermission, true);
    }
  }, [selectedPermissions, currentUserPermissions, isSuperuser]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearPermissionsInTree = (items, preserveCheckedOutside = true) => {
    const subtreeNodes = new Set();

    const collectSubtreeNodes = (nodes) => {
      nodes.forEach((node) => {
        subtreeNodes.add(node);
        if (node.children?.length > 0) {
          collectSubtreeNodes(node.children);
        }
      });
    };
    collectSubtreeNodes(items);

    const keepPermissions = new Set();
    if (preserveCheckedOutside) {
      const collectOutsideCheckedPermissions = (nodes) => {
        nodes.forEach((node) => {
          if (subtreeNodes.has(node)) {
            return;
          }

          const nodePermissions = node.permissions || [];
          if (nodePermissions.length > 0 && getNodeChecked(node)) {
            nodePermissions.forEach((permission) => keepPermissions.add(permission));
          }

          if (node.children?.length > 0) {
            collectOutsideCheckedPermissions(node.children);
          }
        });
      };

      collectOutsideCheckedPermissions(permissionTreeItems);
    }

    const clear = (nodes) => {
      nodes.forEach((item) => {
        (item.permissions || []).forEach((permission) => {
          if (keepPermissions.has(permission)) {
            return;
          }
          setPermissionValueIfChanged(permission, false);
        });

        if (item.children?.length > 0) {
          clear(item.children);
        }
      });
    };

    clear(items);
  };

  const setPermissionsValue = (permissions, value, exclusiveGroup) => {
    if (value && exclusiveGroup && exclusiveGroups[exclusiveGroup]) {
      exclusiveGroups[exclusiveGroup].forEach((groupPermissions) => {
        if (isSamePermissionsSet(groupPermissions, permissions)) {
          return;
        }

        groupPermissions.forEach((permission) => {
          setPermissionValueIfChanged(permission, false);
        });
      });
    }

    permissions.forEach((permission) => {
      setPermissionValueIfChanged(permission, value);
    });
  };

  const getNodeChecked = (item) => {
    const permissions = item.permissions || [];
    if (permissions.length === 0) {
      return false;
    }

    if (item.exclusiveGroup && exclusiveGroups[item.exclusiveGroup]?.length) {
      const matchedOptions = exclusiveGroups[item.exclusiveGroup].filter((groupPermissions) => groupPermissions.every((permission) => Boolean(selectedPermissions[permission])));

      if (!matchedOptions.length) {
        return false;
      }

      // If options overlap by permissions, prefer the most specific (largest) one.
      const activeOption = matchedOptions.reduce((best, current) => {
        if (!best) return current;
        const bestPriority = exclusiveOptionPriority[`${item.exclusiveGroup}::${getPermissionsSetKey(best)}`] || best.length;
        const currentPriority = exclusiveOptionPriority[`${item.exclusiveGroup}::${getPermissionsSetKey(current)}`] || current.length;

        if (currentPriority === bestPriority) {
          return current.length > best.length ? current : best;
        }

        return currentPriority > bestPriority ? current : best;
      }, null);

      return isSamePermissionsSet(activeOption || [], permissions);
    }

    return permissions.every((permission) => Boolean(selectedPermissions[permission]));
  };

  const handleNodeChange = (item, value) => {
    const permissions = item.permissions || [];
    const clearExclusiveGroup = () => {
      if (!item.exclusiveGroup || !exclusiveGroups[item.exclusiveGroup]) {
        return;
      }

      exclusiveGroups[item.exclusiveGroup].forEach((groupPermissions) => {
        groupPermissions.forEach((permission) => {
          setPermissionValueIfChanged(permission, false);
        });
      });
    };

    if (isFullVacancyAccessActive && isLimitedVacancyAccessItem(item)) {
      // Clicking "Limited access" while "Full access" is active switches mode to limited.
      setPermissionsValue(permissions, true, item.exclusiveGroup);
      return;
    }

    if (!value && isFullVacancyAccessItem(item)) {
      setPermissionsValue(permissions, false, item.exclusiveGroup);
      if (limitedVacancyAccessNode) {
        clearPermissionsInTree([limitedVacancyAccessNode], false);
      }
      return;
    }

    if (!value && item.exclusiveGroup && exclusiveGroups[item.exclusiveGroup]) {
      clearExclusiveGroup();
      return;
    }

    setPermissionsValue(permissions, value, item.exclusiveGroup);

    if (!value && item.children?.length > 0) {
      clearPermissionsInTree(item.children);
    }
  };

  useEffect(() => {
    const walk = (items, parentChecked = true) => {
      items.forEach((item) => {
        const permissions = item.permissions || [];
        const hasCheckbox = permissions.length > 0;
        const isChecked = hasCheckbox ? getNodeChecked(item) : true;
        const isEnabledByParent = parentChecked;
        const canEnableChildren = isEnabledByParent && (!hasCheckbox || isChecked);
        const keepChildrenEnabledForFullVacancy = isFullVacancyAccessActive && isLimitedVacancyAccessItem(item);
        const effectiveCanEnableChildren = keepChildrenEnabledForFullVacancy ? true : canEnableChildren;

        if (!effectiveCanEnableChildren && item.children?.length > 0) {
          clearPermissionsInTree(item.children);
        }

        if (item.children?.length > 0) {
          walk(item.children, effectiveCanEnableChildren);
        }
      });
    };

    walk(permissionTreeItems, true);
  }, [selectedPermissions, isFullVacancyAccessActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderTreeNode = (item, keyPrefix = 'root', parentChecked = true, forceDisabledByFullVacancy = false) => {
    const nodeKey = `${keyPrefix}-${item.label}`;
    const permissions = item.permissions || [];
    const hasCheckbox = permissions.length > 0;
    const isLimitedVacancyNode = isLimitedVacancyAccessItem(item);
    const disableChildrenByFullVacancy = forceDisabledByFullVacancy || (isFullVacancyAccessActive && isLimitedVacancyNode);
    const isCurrentNodeForcedDisabledByFullVacancy = forceDisabledByFullVacancy && !isLimitedVacancyNode;
    const isChecked = hasCheckbox ? getNodeChecked(item) : false;
    const isDisabled = hasCheckbox ? !parentChecked || isCurrentNodeForcedDisabledByFullVacancy || getIsDisabled(permissions) : true;
    const canEnableChildren = parentChecked && (!hasCheckbox || isChecked);
    const safeKey = nodeKey.replace(/[^a-zA-Z0-9-_]/g, '-');
    const inputId = `${name}-${permissions.join('-')}-${safeKey}`;

    return (
      <div key={nodeKey} className="space-y-1 select-none">
        {hasCheckbox ? (
          <div className={`relative flex items-start gap-x-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            <div className="flex h-5 items-center">
              <Controller
                control={form.control}
                name={`${name}.${permissions[0]}`}
                render={({ field: { ref, onBlur } }) => (
                  <input
                    type="checkbox"
                    id={inputId}
                    ref={ref}
                    onBlur={onBlur}
                    checked={isChecked}
                    onChange={(e) => handleNodeChange(item, e.target.checked)}
                    disabled={isDisabled}
                    className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                )}
              />
            </div>
            <div className="text-sm leading-5">
              <label htmlFor={inputId} className={`font-medium ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-800 cursor-pointer'}`}>
                {item.label}
              </label>
            </div>
          </div>
        ) : (
          <p className={`text-[13px] font-medium leading-5 ${parentChecked && !forceDisabledByFullVacancy ? 'text-gray-600' : 'text-gray-400'}`}>{item.label}</p>
        )}

        {item.children?.length > 0 && (
          <div className={item.noChildrenIndent ? 'space-y-1' : 'ml-4 pl-3 border-l border-gray-200 space-y-1'}>
            {item.children.map((child) => renderTreeNode(child, nodeKey, canEnableChildren, disableChildrenByFullVacancy))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <p className="text-[12px] font-semibold uppercase tracking-wide text-gray-500">Полномочия пользователя</p>
      {filteredPermissionGroups.map((group) => (
        <div key={group.title} className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{group.title}</p>
          <div className="space-y-1">
            {group.items.map((item) => (
              <React.Fragment key={`${group.title}-${item.label}`}>{renderTreeNode(item, group.title)}</React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
